import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'
import ProductsCombo from '@/widgets/ProductsCombo'
import { getFormPrice } from '@/pages/system/Settings/components/ProductFormModal'

const getAssignedProductId = row => (row?.productId?._id || row?.productId || '').toString().trim()
const getAssignedProductName = row => (row?.productId?.name || row?.name || '').trim()

const fieldInputClassName =
	'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'

export default function ProductTable({
	products,
	setProduct,
	addRow,
	removeRow,
	fmtMoney,
	readOnly = false,
	className = 'flex lg:col-span-2 px-2 md:px-0',
	onAssignProduct,
}) {
	const { t } = useTranslation()
	const canAssignProduct = readOnly && typeof onAssignProduct === 'function'

	// Prevent accidental value change on scroll
	const stopWheel = useCallback(e => e.currentTarget.blur(), [])

	// Single change handler using dataset (avoids per-cell closures)
	const handleChange = useCallback(
		e => {
			const idx = Number(e.currentTarget.dataset.index)
			const field = e.currentTarget.dataset.field
			const value = e.currentTarget.value
			setProduct(idx, { [field]: value })
		},
		[setProduct]
	)

	const handleArticleNumberChange = useCallback(
		(rowIndex, value) => {
			setProduct(rowIndex, { article_number: value })
		},
		[setProduct]
	)

	// Single remove handler via dataset
	const handleRemove = useCallback(
		e => {
			const idx = Number(e.currentTarget.dataset.index)
			removeRow(idx)
		},
		[removeRow]
	)

	// Handle product selection from combo (fills all settings catalog fields)
	const handleProductSelect = useCallback(
		(rowIndex, product) => {
			if (!product) {
				setProduct(rowIndex, {
					name: '',
					sku: '',
					article_number: '',
					productId: '',
				})
				return
			}

			const patch = {
				name: product?.name || '',
				sku: product?.sku ?? '',
				article_number: product?.article_number ?? '',
				productId: product?._id || '',
			}

			const price = getFormPrice(product)
			if (price !== '') patch.price = price
			if (product?.taxPercent != null && product?.taxPercent !== '') {
				patch.taxPercent = product.taxPercent
			}

			setProduct(rowIndex, patch)
		},
		[setProduct]
	)

	const articleNumberOptions = [
		{ value: '', label: t('settingsPage.products.fields.article_numberNone') },
		{ value: '43', label: '43' },
		{ value: '113', label: '113' },
	]

	const renderAssignedProductCell = (row, index) => {
		const productId = getAssignedProductId(row)
		const productName = getAssignedProductName(row)
		const hasAssignedProduct = Boolean(productId)

		if (hasAssignedProduct) {
			return (
				<div className="flex items-center gap-2 py-2">
					<span className="text-sm text-gray-900 break-words">{productName || productId}</span>
					<button
						type="button"
						onClick={() => onAssignProduct(index, row)}
						className="rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50 shrink-0">
						{t('invoices.list.table.changePlacowka')}
					</button>
				</div>
			)
		}

		return (
			<button
				type="button"
				onClick={() => onAssignProduct(index, row)}
				className="py-2 text-sm text-blue-600 hover:text-blue-800 hover:underline">
				{t('invoices.list.table.assignPlacowka')}
			</button>
		)
	}

	const renderArticleNumberCell = (row, index) => {
		if (readOnly) {
			return <div className="py-2 text-sm">{row.article_number || '-'}</div>
		}

		const selectedOption = row.article_number
			? { value: String(row.article_number), label: String(row.article_number) }
			: articleNumberOptions[0]

		return (
			<CreatableSelect
				name={`article_number_${index}`}
				options={articleNumberOptions}
				value={selectedOption}
				onChange={option => handleArticleNumberChange(index, option?.value ?? '')}
				onCreateOption={value => handleArticleNumberChange(index, value)}
				placeholder={t('settingsPage.products.fields.article_numberPlaceholder')}
				formatCreateLabel={value =>
					t('settingsPage.products.fields.createArticleNumber', { value })
				}
				className="react-select min-w-[10ch]"
				classNamePrefix="react-select"
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				styles={{
					control: (base, state) => ({
						...base,
						minHeight: 38,
						borderColor: state.isFocused ? '#6366f1' : '#d1d5db',
						boxShadow: state.isFocused ? '0 0 0 1px #6366f1' : 'none',
						'&:hover': { borderColor: state.isFocused ? '#6366f1' : '#9ca3af' },
					}),
					menuPortal: base => ({ ...base, zIndex: 40 }),
				}}
			/>
		)
	}

	return (
		<div className={className}>
			<div className="flex h-full w-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
				<div className="border-b border-gray-200 p-6">
					<h2 className="text-lg font-semibold text-gray-900">{t('invoices.form.products.title')}</h2>
				</div>

				<div className="p-6 max-md:-mx-4">
					<div
						className="
                  max-md:overflow-x-auto max-md:px-4
                  md:[&_table]:min-w-0 md:[&_table]:w-full
                  md:[&_col]:w-auto md:[&_col]:basis-auto
                  md:[&_.overflow-x-auto]:overflow-visible
                ">
						<div className="px-4 sm:px-0">
							<div className="-mx-4 sm:mx-0 overflow-x-auto">
								{canAssignProduct ? (
									<table className="w-max min-w-[920px] lg:w-full bg-white border-t border-b border-gray-200 rounded-lg">
										<colgroup>
											<col className="w-[28%]" />
											<col className="w-[22%]" />
											<col className="w-[14ch]" />
											<col className="w-[10ch]" />
											<col className="w-[10ch]" />
											<col className="w-[16ch]" />
											<col className="w-[1%]" />
										</colgroup>
										<thead className="bg-gray-50">
											<tr>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
													{t('invoices.form.products.infaktName')}
												</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
													<Link
														to="/settings?tab=products"
														className="text-blue-600 hover:text-blue-800 hover:underline">
														{t('invoices.form.products.localProduct')}
													</Link>
												</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.price')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.count')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.vatPercent')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.total')}</th>
												<th className="px-3 py-2" />
											</tr>
										</thead>
										<tbody>
											{products.map((row, i) => (
												<tr key={i} className="border-t border-gray-200">
													<td className="px-3 py-2 align-top">
														<div className="py-2.5 text-sm break-words">{row.name}</div>
													</td>
													<td className="px-3 py-2 align-top">
														{renderAssignedProductCell(row, i)}
													</td>
													<td className="px-3 py-2">
														<div className="py-2 text-sm tabular-nums">{row.price}</div>
													</td>
													<td className="px-3 py-2">
														<div className="py-2 text-sm tabular-nums">{row.count}</div>
													</td>
													<td className="px-3 py-2">
														<div className="py-2 tabular-nums">{row.taxPercent}</div>
													</td>
													<td className="px-3 py-2">
														<div className="text-sm font-semibold tabular-nums whitespace-nowrap">{fmtMoney(row.total)}</div>
													</td>
													<td className="px-3 py-2" />
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<table className="w-max min-w-[900px] lg:w-full bg-white border-t border-b border-gray-200 rounded-lg">
										<colgroup>
											<col className="w-[18%]" />
											<col className="w-[18%]" />
											<col className="w-[14ch]" />
											<col className="w-[10ch]" />
											<col className="w-[10ch]" />
											<col className="w-[16ch]" />
											<col className="w-[1%]" />
										</colgroup>
										<thead className="bg-gray-50">
											<tr>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
													{t('settingsPage.products.fields.sku')}
												</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">
													{t('settingsPage.products.fields.article_number')}
												</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.price')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.count')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.vatPercent')}</th>
												<th className="px-3 py-2 text-left text-sm font-medium text-gray-700">{t('invoices.form.products.total')}</th>
												<th className="px-3 py-2" />
											</tr>
										</thead>
										<tbody>
											{products.map((row, i) => (
												<React.Fragment key={i}>
													<tr className="border-t border-gray-200">
														<td colSpan={7} className="px-3 pt-3 pb-1">
															<div className="flex items-start gap-3">
																<div className="min-w-0 flex-1">
																	<label className="mb-1 block text-xs font-medium text-gray-500">
																		{t('invoices.form.products.name')}
																	</label>
																	{readOnly ? (
																		<div className="py-1.5 text-sm break-words text-gray-900">{row.name}</div>
																	) : (
																		<ProductsCombo
																			selectedId={getAssignedProductId(row)}
																			selectedProductName={row.name || ''}
																			onChange={product => handleProductSelect(i, product)}
																			createDefaults={{
																				sku: row.sku,
																				article_number: row.article_number,
																				price: row.price,
																				taxPercent: row.taxPercent,
																			}}
																		/>
																	)}
																</div>
																{!readOnly && products.length > 1 && (
																	<button
																		type="button"
																		data-index={i}
																		onClick={handleRemove}
																		className="mt-6 shrink-0 px-3 py-1.5 text-sm bg-red-50 text-red-700 rounded hover:bg-red-100"
																		aria-label={t('invoices.form.products.removeRowAria', { index: i + 1 })}>
																		{t('invoices.form.products.remove')}
																	</button>
																)}
															</div>
														</td>
													</tr>
													<tr className="border-b border-gray-100 last:border-b-0">
														<td className="px-3 py-2 align-top">
															{readOnly ? (
																<div className="py-2 text-sm">{row.sku || '-'}</div>
															) : (
																<input
																	type="text"
																	data-index={i}
																	data-field="sku"
																	value={row.sku || ''}
																	onChange={handleChange}
																	className={`${fieldInputClassName} min-w-[8ch]`}
																	aria-label={t('settingsPage.products.fields.sku')}
																/>
															)}
														</td>
														<td className="px-3 py-2 align-top">
															{renderArticleNumberCell(row, i)}
														</td>
														<td className="px-3 py-2">
															{readOnly ? (
																<div className="py-2 text-sm tabular-nums">{row.price}</div>
															) : (
																<input
																	type="number"
																	step="0.01"
																	data-index={i}
																	data-field="price"
																	value={row.price}
																	onChange={handleChange}
																	onWheel={stopWheel}
																	className={`${fieldInputClassName} sm:max-w-[14ch] min-w-[12ch] sm:min-w-0`}
																	placeholder="0"
																	inputMode="decimal"
																	aria-label={t('invoices.form.products.priceAria')}
																/>
															)}
														</td>
														<td className="px-3 py-2">
															{readOnly ? (
																<div className="py-2 text-sm tabular-nums">{row.count}</div>
															) : (
																<input
																	type="number"
																	step="1"
																	data-index={i}
																	data-field="count"
																	value={row.count}
																	onChange={handleChange}
																	onWheel={stopWheel}
																	className={`${fieldInputClassName} sm:max-w-[9ch] min-w-[7ch] sm:min-w-0 tabular-nums`}
																	placeholder="0"
																	inputMode="numeric"
																	aria-label={t('invoices.form.products.countAria')}
																/>
															)}
														</td>
														<td className="px-3 py-2">
															{readOnly ? (
																<div className="py-2 tabular-nums">{row.taxPercent}</div>
															) : (
																<input
																	type="number"
																	step="0.01"
																	data-index={i}
																	data-field="taxPercent"
																	value={row.taxPercent}
																	onChange={handleChange}
																	onWheel={stopWheel}
																	className={`${fieldInputClassName} sm:max-w-[9ch] min-w-[7ch] sm:min-w-0 tabular-nums`}
																	placeholder="0"
																	inputMode="decimal"
																	aria-label={t('invoices.form.products.taxPercentAria')}
																/>
															)}
														</td>
														<td className="px-3 py-2">
															<div className="text-sm font-semibold tabular-nums whitespace-nowrap">{fmtMoney(row.total)}</div>
														</td>
														<td className="px-3 py-2" />
													</tr>
												</React.Fragment>
											))}
										</tbody>
									</table>
								)}
							</div>

							{!readOnly && (
								<div className="mt-3">
									<button type="button" onClick={addRow} className="px-4 py-2 bg-gray-100 rounded hover:bg-gray-200">
										{t('invoices.form.products.addProduct')}
									</button>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
